import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { fpMultiply, toNumber } from '@/helpers/unit-formatter/unit-formatter.utils';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Transforms temperature values from Celsius to other temperature scales.
 * Supports conversion to Fahrenheit (°F) and Kelvin (°K).
 * Uses floating-point safe arithmetic operations for accurate conversions.
 *
 * @param {UnitValue} unitValue - The temperature value in Celsius
 * @param {any} unitValue.value - The numeric temperature value in Celsius
 * @param {string} [unitValue.units] - The current units (should be °C or similar)
 * @param {string} [newUnits] - Target temperature scale ('°F', '°K')
 * @returns {UnitValue} Transformed temperature value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined
 *
 * @example
 * transformCelsius({ value: 0, units: '°C' }, '°F')
 * // Returns: { value: 32, units: '°F' }
 *
 * @example
 * transformCelsius({ value: 100, units: '°C' }, '°F')
 * // Returns: { value: 212, units: '°F' }
 *
 * @example
 * transformCelsius({ value: 0, units: '°C' }, '°K')
 * // Returns: { value: 273.15, units: '°K' }
 */
export const transformCelsius = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const value = toNumber(unitValue.value);
  if (value == null || value == undefined) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.DEGREES_F:
      return {
        value: fpMultiply(value, 9 / 5) + 32,
        units: newUnits,
      };
    case SUPPORTED_UNITS.DEGREES_K:
      return {
        value: value + 273.15,
        units: newUnits,
      };
    default:
      return unitValue;
  }
};
